import { Component, EventEmitter, Input, Output } from "@angular/core";

import { AuthService } from "src/app/core/core-services/services/auth.service";
import {
  OnboardingProgressService,
  OnboardingStepKey,
  REQUIRED_ONBOARDING_STEPS,
  WELCOME_MODAL_STEP_ORDER,
} from "src/app/core/core-services/services/onboarding-progress.service";

interface ChecklistRow {
  /** WelcomeModalComponent panel index (0-7) — the modal's *ngSwitchCase this row opens. */
  index: number;
  key: OnboardingStepKey;
  label: string;
  /** Issue #520: install_extension/about_you only — renders the row's red asterisk. */
  required: boolean;
}

const ROW_LABELS: Readonly<Record<OnboardingStepKey, string>> = {
  install_extension: "Install the Extension",
  about_you: "About You",
  add_flycut_signature: "Add a FlyCut",
  test_flycut: "Test a FlyCut",
  add_flyplate: "Add a FlyPlate",
  create_persona: "Create a Persona",
  linkedin_ai_comment_generator: "LinkedIn AI Comment Generator",
  linkedin_social_post_generator: "LinkedIn Social Post Generator",
};

/**
 * The 8 rows the welcome-modal wizard has UI for, in display order —
 * derived from the shared `WELCOME_MODAL_STEP_ORDER` (the same array
 * WelcomeModalComponent uses to map its panel index to a step key) so the two
 * can never drift apart. Issue #519 inserted `about_you` at index 1, right
 * after Install the Extension.
 */
const CHECKLIST_ROWS: ChecklistRow[] = WELCOME_MODAL_STEP_ORDER.map((key, index) => ({
  index,
  key,
  label: ROW_LABELS[key],
  required: (REQUIRED_ONBOARDING_STEPS as readonly OnboardingStepKey[]).includes(key),
}));

/** FlyLearning's row index in the modal's panel switch (case 8) — see welcome-modal.component.html. */
const FLY_LEARNING_STEP_INDEX = WELCOME_MODAL_STEP_ORDER.length;

/**
 * The welcome-modal's left-hand checklist (the 7 tracked rows + the
 * plan-gated FlyLearning row) plus the "More learning videos" ghost button.
 *
 * Issue #523's enabling refactor: this list used to be copy-pasted 9x, once
 * per `welcome-modal.component.html` `*ngSwitchCase`. `checked` now reads
 * `OnboardingProgressService.isCompleted()` (real per-step completion —
 * skips no longer count) instead of the monotonic `onboardingv2_step`
 * comparison; `enable` (row reachability/sequencing) is intentionally left on
 * `onboardingv2_step`, unchanged — that counter still advances on every
 * `completeStep`/`goToStep` call, so which rows are reachable is unaffected.
 * The FlyLearning row is excluded from the tracked percentage by design (see
 * the issue), so it keeps its pre-#523 `onboardingv2_step`-based
 * checked/enabled logic rather than moving onto the new completed-set model.
 *
 * The host element carries the `fms-walk-me-left-panel` class directly (see
 * the `host` metadata) so this component drops straight into the parent's
 * `.fms-walk-me-content` flex row exactly where the inlined `<section
 * class="fms-walk-me-left-panel">` used to sit — no extra wrapper markup.
 */
@Component({
    selector: "app-onboarding-checklist",
    templateUrl: "./onboarding-checklist.component.html",
    styleUrls: ["./onboarding-checklist.component.scss"],
    host: { class: "fms-walk-me-left-panel" },
    standalone: false
})
export class OnboardingChecklistComponent {
  /** WelcomeModalComponent's current panel index — the row that renders `active`. */
  @Input() activeStep: number;
  @Output() readonly stepSelected = new EventEmitter<number>();
  @Output() readonly moreLearningVideos = new EventEmitter<void>();

  readonly rows = CHECKLIST_ROWS;
  readonly flyLearningStepIndex = FLY_LEARNING_STEP_INDEX;

  constructor(
    private readonly authService: AuthService,
    public readonly onboardingProgress: OnboardingProgressService
  ) {}

  get user() {
    return this.authService.userDetails.user_details;
  }
}
